import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { getInvoiceLogs, type InvoiceLog } from '@/api/salesInvoice';
import { InvoiceDetailModal } from '@/pages/sales/sales-invoice/InvoiceDetailModal';
import { RegisterTable, type RegisterCol } from './RegisterTable';
import { useAuth } from '@/auth/useAuth';
import { ACTION_TONE, logDay, logTime, prettyRole } from './invoiceLogFormat';

const ACTIONS = ['Created', 'Edited', 'Issued', 'Payment recorded', 'Cancelled', 'Attachment added', 'Attachment removed'];

/**
 * Invoice Logs — every change to every GST invoice: when (date, day, time), which
 * invoice, what happened, exactly what changed (old → new), why, by whom and from
 * which IP. Read-only; rows are written by the invoice actions themselves.
 */
export function InvoiceLogsPage() {
  const period = usePeriod();
  const { range } = period;
  const [action, setAction] = useState('');
  const [user, setUser] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [exportTick, setExportTick] = useState(0);
  // Invoice Logs can be granted on its own; opening an invoice needs the Invoice permission.
  const canOpen = useAuth().hasPerm('sp-invoice');

  const query = useQuery({
    queryKey: ['invoice-logs', range?.from ?? '', range?.to ?? '', action],
    queryFn: () => getInvoiceLogs({ from: range?.from, to: range?.to, action: action || undefined }),
  });
  const all = useMemo(() => query.data ?? [], [query.data]);
  const users = useMemo(() => [...new Set(all.map((l) => l.actorName).filter(Boolean) as string[])].sort(), [all]);
  const rows = useMemo(() => all.filter((l) => !user || l.actorName === user), [all, user]);

  const columns: RegisterCol<InvoiceLog>[] = [
    { key: 'at', header: 'Date & time', cell: (l) => <span className="tabular-nums">{logTime(l.at)}</span>, value: (l) => logTime(l.at) },
    { key: 'day', header: 'Day', cell: (l) => logDay(l.at), value: (l) => logDay(l.at) },
    {
      key: 'invoiceNo', header: 'Invoice', value: (l) => l.invoiceNo,
      cell: (l) => canOpen
        ? <button type="button" className="font-extrabold text-accent hover:underline" onClick={() => setOpenId(l.invoiceId)}>{l.invoiceNo}</button>
        : <span className="font-extrabold text-accent">{l.invoiceNo}</span>,
    },
    {
      key: 'action', header: 'Action', align: 'center', value: (l) => l.action,
      cell: (l) => <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${ACTION_TONE[l.action] ?? 'bg-[var(--bg-soft)] text-[var(--text-secondary)]'}`}>{l.action}</span>,
    },
    {
      key: 'changes', header: 'What changed (old → new)',
      value: (l) => l.changes.map((c) => `${c.label}: ${c.from || '—'} → ${c.to || '—'}`).join('; '),
      cell: (l) => l.changes.length ? (
        <div className="min-w-[260px] space-y-0.5 whitespace-normal text-xs">
          {l.changes.map((c, i) => (
            <div key={i}>
              <span className="font-bold text-[var(--text-secondary)]">{c.label}:</span>{' '}
              {c.from ? <span className="text-danger line-through decoration-1">{c.from}</span> : null}
              {c.from ? ' → ' : ''}<span className="font-bold text-success">{c.to || '—'}</span>
            </div>
          ))}
        </div>
      ) : <span className="text-xs text-[var(--text-muted)]">—</span>,
    },
    { key: 'reason', header: 'Reason', value: (l) => l.reason ?? '', cell: (l) => <span className="block min-w-[160px] whitespace-normal text-xs">{l.reason || '—'}</span> },
    {
      key: 'by', header: 'Changed by', value: (l) => `${l.actorName ?? 'System'}${l.actorRole ? ` (${prettyRole(l.actorRole)})` : ''}`,
      cell: (l) => <span>{l.actorName ?? 'System'}{l.actorRole && <span className="block text-[10px] font-normal text-[var(--text-muted)]">{prettyRole(l.actorRole)}</span>}</span>,
    },
  ];

  return (
    <div>
      <SectionHeader title="Invoice Logs" />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice, change, reason" className="!pl-5 sm:!pl-8" aria-label="Search invoice logs" />
          </div>
        </FilterField>
        <FilterField label="Action">
          <Select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {ACTIONS.map((a) => <option key={a}>{a}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Changed by">
          <Select value={user} onChange={(e) => setUser(e.target.value)}>
            <option value="">Everyone</option>
            {users.map((u) => <option key={u}>{u}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <button
          type="button"
          title="Export to Excel — every log row matching these filters and search"
          onClick={() => setExportTick((t) => t + 1)}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:gap-1.5 sm:self-end sm:px-3 sm:text-xs"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <RegisterTable
        title="Invoice changes"
        count={`${rows.length} change${rows.length === 1 ? '' : 's'}`}
        columns={columns}
        rows={rows}
        rowKey={(l) => l.id}
        loading={query.isLoading}
        search={search}
        searchText={(l) => [l.invoiceNo, l.action, l.reason, l.actorName, ...l.changes.flatMap((c) => [c.label, c.from, c.to])].join(' ')}
        exportSignal={exportTick}
        exportName="Invoice_Logs"
      />

      <InvoiceDetailModal open={openId != null} invoiceId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
